import { useContext, useEffect } from 'react'
import { FieldArrayWithId, useFieldArray, useFormContext } from 'react-hook-form'

import { Table } from 'components/Table'
import { useSort } from 'components/Table/Sort'
import { useWorkspaceParam } from 'features/workspace/hooks/useWorkspaceParam'
import { ViewportPageContext } from '../../contexts/ViewportPageContext'
import { useGetViewports } from '../../hooks/useGetViewports'
import { DEFAULT_SORT, SORTABLE_COLUMNS } from './constants'
import { usePrependOnAddNewButton } from './hooks/usePrependOnAddNewButton'
import { useColumns } from './useColumns'

import type { Schema } from '../../forms/ViewportForm/schema'
import type { ViewportSlice } from '../../hooks/useGetViewports'
import type { ViewportSortBy } from './types'
import type { ColumnDef } from '@tanstack/react-table'

export const ViewportTable = (): React.JSX.Element => {
  const { workspaceId } = useWorkspaceParam()
  const { filters } = useContext(ViewportPageContext)
  const { props: sortProps, sort } = useSort<FieldArrayWithId<ViewportSlice>, ViewportSortBy>(
    DEFAULT_SORT,
    SORTABLE_COLUMNS,
  )

  const { loading, viewports } = useGetViewports({ sort, workspaceId })

  const { control, reset } = useFormContext<Schema>()
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: 'viewports',
  })
  usePrependOnAddNewButton({ prepend })

  const columns: ColumnDef<FieldArrayWithId<ViewportSlice>>[] = useColumns({ remove })

  useEffect(() => {
    if (viewports !== undefined) {
      reset({ viewports })
    }
  }, [loading, sort])

  return (
    <Table<FieldArrayWithId<ViewportSlice>>
      columns={columns}
      data={fields}
      filters={filters}
      loading={loading}
      meta={{ stickyHeader: true }}
      sort={sortProps}
    />
  )
}
